import { Localized } from "@fluent/react"
import { LoadingButton } from "@mui/lab"
import { Alert, Box, Divider, Paper, Stack, Typography } from "@mui/material"
import { Container } from "@mui/system"
import { createAsyncThunk, unwrapResult } from "@reduxjs/toolkit"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { AppThunkAPIType } from "../../app/appTypes"
import { EmailTextField } from "../../app/components/EmailTextField"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { get, post, postWithAuth } from "../../http"
import ExpiredLinkPanel from "../../app/ExpiredLinkPanel"
import { VChainerFooter } from "../../app/components/VChainerFooter"
import { DocumentAccessLinkSentPanel } from "./pages/DocumentAccessLinkSentPanel"
import { DocumentAwaitingApprovalPanel } from "./pages/DocumentAwaitingApprovalPanel"
import { selectSettings } from "../settings/settingsSlice"
import { If } from "../../app/If"
import { LanguageToggle } from "../../app/components/LanguageToggle"

interface ObjectDescription {
    objectId: string
    objectType: string
    ownerName: string
    structureName: string
    structureNameEN: string
}

interface ValidateResponse {
    isValid: boolean
    expiresAt: string | null
    token: string
    objectDescription: ObjectDescription | null
}

const validate = createAsyncThunk<ValidateResponse, string, AppThunkAPIType>("publish-document/validate-token", async (token, { dispatch, rejectWithValue }) => {
    const result = await dispatch(get({ url: `api/public-access/token/validate?Token=${token}`, headers: {} }))
    const { payload } = result
    if (get.fulfilled.match(result)) {
        return payload as ValidateResponse
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

interface GuestResponse {
    token: string | null
    accessGranted: boolean
    awaitingApproval: boolean
    confirmationMailSend: boolean
}
const requestGuestToken = createAsyncThunk<GuestResponse, { token: string, email: string }, AppThunkAPIType>("publish-document/request-guest-token",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await post("api/public-access/token/use", arg)

            if (response.ok) {
                return await response.json() as GuestResponse
            }

            return rejectWithValue({
                kind: "http",
                status: response.status,
                body: await response.text(),
            });

        } catch (error) {
            return rejectWithValue({
                kind: 'connection',
            })
        }
    })

type ViewState = 'empty-token' | 'validating' | 'invalid' | 'ok' | 'wait' | 'wrong' | 'mail-sent'

export const PublishDocument = () => {
    const { token } = useParams()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { locale } = useAppSelector(selectSettings);

    const [state, setState] = useState<ViewState>(token ? 'validating' : 'empty-token')
    const [email, setEmail] = useState("")
    const [showError, setShowError] = useState(false)
    const [loading, setLoading] = useState(false)
    const [documentInfo, setDocumentInfo] = useState<ObjectDescription | null>(null)

    useEffect(() => {
        if (token) {
            dispatch(validate(token))
                .then(unwrapResult)
                .then(r => {
                    if (r.isValid) {
                        setDocumentInfo(r.objectDescription)
                        setState('ok')
                    } else {
                        setState('invalid')
                    }
                })
                .catch(err => {
                    console.log(err)
                    setState('invalid')
                })
        }
    }, [token])

    const handleGuestClick = async () => {
        if (token && email) {
            setLoading(true)
            try {
                const rs = await dispatch(requestGuestToken({ token, email })).unwrap()
                if (rs.awaitingApproval) {
                    setState('wait')
                } else if (rs.accessGranted && rs.confirmationMailSend) {
                    setState('mail-sent')
                } else {
                    setState('wrong')
                }
            } catch (error) {
                console.log(error)
            } finally {
                setLoading(false)
            }
        } else {
            setShowError(true)
        }
    }

    const handleAppUserClick = () => {
        navigate(`/resolve-token/${token}`)
    }

    let content = <></>
    switch (state) {
        case 'empty-token':
            content = <Alert severity="error">
                <Typography component='div' variant="h2">
                    <Localized id='publish-doc-empty-token'>Token jest pusty</Localized>
                </Typography>
            </Alert>
            break
        case 'validating':
            content = <Typography component='div' variant="h2">
                <Localized id='publish-doc-validating-token'>Weryfikacja tokena. Proszę czekać...</Localized>
            </Typography>
            break
        case 'invalid':
            content = <ExpiredLinkPanel />
            break
        case 'ok':
            content = <Stack component={Paper}
                sx={{
                    position: 'relative',
                    alignItems: 'center',
                    p: 5,
                    width: { xs: '94vw', sm: 600, md: 700, lg: 800 },
                    maxWidth: '100%',
                }}
                direction='column' gap={2}>
                <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                    <LanguageToggle />
                </Box>
                <Typography variant="h4" textAlign="center">
                    <Localized id='urs-comment-document'>Skomentuj dokument URS</Localized>
                </Typography>
                <Stack spacing={0.5}>
                    <Typography variant="subtitle1">
                        <Localized id='document-document-company-owner'>Wystawca dokumentu</Localized> : <strong>{documentInfo?.ownerName}</strong>
                    </Typography>
                    <Typography variant="subtitle1">
                        <Localized id='device'>Urządzenia</Localized> : <strong>{documentInfo?.structureNameEN} / {documentInfo?.structureName} </strong>
                    </Typography>
                </Stack>
                <Divider sx={{ width: '100%', my: 1 }} />

                <If condition={locale === 'pl'}>
                    <Typography variant="subtitle1" color="text.secondary">
                        Jeśli jesteś użytkownikiem aplikacji V-Chainer, kliknij przycisk <strong>„Użytkownik aplikacji”</strong>.<br />
                        W przeciwnym razie wybierz <strong>„Gość”</strong> — konieczne będzie podanie adresu e-mail.
                    </Typography>
                </If>
                <If condition={locale !== 'pl'}>
                    <Typography variant="subtitle1" color="text.secondary">
                        If you are a V-Chainer application user, click the <strong>“Application User”</strong> button.<br />
                        Otherwise, select <strong>“Guest”</strong> — you will need to provide an email address.
                    </Typography>
                </If>
                <LoadingButton loading={loading} sx={{ width: '100%' }} variant="contained" onClick={handleAppUserClick}>
                    <Localized id='application-user'>Użytkownik aplikacji</Localized>
                </LoadingButton>
                <Typography component="div" variant="body1">
                    <Localized id='or'>lub</Localized>
                </Typography>
                <Stack sx={{ p: 1, width: '100%' }} gap={1} >
                    <EmailTextField value={email} required onChange={e => setEmail(e.target.value)} showError={showError} />
                    <LoadingButton loading={loading} variant="contained" onClick={handleGuestClick}>
                        <Localized id='guest'>Gość</Localized>
                    </LoadingButton>
                </Stack>
                <VChainerFooter />
            </Stack>
            break
        case 'wait':
            content = <DocumentAwaitingApprovalPanel />
            break
        case 'wrong':
            content = <Paper sx={{ p: 4 }}>
                <Typography variant="h3">
                    Błąd - brak dostępu i prośba o udostępnienie nie została wysłana
                </Typography>
            </Paper>
            break
        case 'mail-sent':
            content = <DocumentAccessLinkSentPanel />
    }

    return <Container sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: "#f4f6f8",
        height: '100vh',
    }} component={Paper} maxWidth={false} >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {content}
        </Box>

    </Container>
}

interface ResolveResponse {
    token: string | null
    accessGranted: boolean
    awaitingApproval: boolean
    objectId: string
}
const resolveToken = createAsyncThunk<ResolveResponse, { token: string }, AppThunkAPIType>("publish-document/resolve-token",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const result = await dispatch(postWithAuth({
                url: "api/public-access/token/resolve",
                payload: arg,
            }))
            const { payload } = result
            if (postWithAuth.fulfilled.match(result)) {
                return payload as ResolveResponse
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }

        } catch (error) {
            return rejectWithValue({
                kind: 'connection',
            })
        }
    })

export const ResolveAccess = () => {
    const { token } = useParams()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    useEffect(() => {
        if (token) {
            dispatch(resolveToken({ token }))
                .then(unwrapResult)
                .then(rs => {
                    if (rs.accessGranted && rs.objectId) {
                        navigate(`/ursOffers/edit/${rs.objectId}`)
                    } else {
                        // TODO: hmmmmm
                    }
                })
                .catch(err => {
                    console.log(err)
                })
        }
    }, [])

    return <Container sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: "#f4f6f8",
        height: '100vh',
    }} component={Paper} maxWidth={false} >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography component='div' variant="h2">
                Trwa przekierowywanie. Proszę czekać...
            </Typography>
        </Box>
    </Container>
}


